<template>
  <div v-if="printer && scan">
    <h1>Scan</h1>
    <v-btn :to="computedScannerLink">
      <v-icon>chevron_left</v-icon>
      {{`Scanner ${printer.code} ${printer.name}`}}
    </v-btn>
    <EntityWorking :entity="entity" v-if="entity" />
    <FileWorking :file="file" v-if="file" />
    <div class="ajaxForm" v-if="$route.query.objectType">
      <h3>Save scan</h3>
      <v-form ref="formUpload" v-model="valid" lazy-validation>
        <v-combobox
          v-model="description"
          :items="descriptions"
          label="Description"
          no-filter
          @input.native="completeDescription($event.srcElement.value)"
          placeholder="Enter description of document"
          :rules="descriptionRules"
          outline
        />
        <v-checkbox label="Show on tracker" v-model="tracker" v-if="file" />
        <v-checkbox label="Save to common" v-model="common" />
        <v-btn @click="saveScan">
          <v-icon>save</v-icon>Save
        </v-btn>
      </v-form>
    </div>
    <table class="scanNav" v-if="navigation">
      <tr>
        <td>
          <v-btn :to="computedPrevious" v-if="computedPrevious">
            <v-icon>chevron_left</v-icon>
            <span>{{navigation.previous.name}}</span>
          </v-btn>
        </td>
        <td>
          <v-btn :to="computedNext" v-if="computedNext">
            <span>{{navigation.next.name}}</span>
            <v-icon>chevron_right</v-icon>
          </v-btn>
        </td>
      </tr>
    </table>
    <table border="1">
      <tr>
        <th>Name</th>
        <td>{{scan.name}}</td>
      </tr>
      <tr>
        <th>Date</th>
        <td>{{scan.lastModified | formatDateTime}}</td>
      </tr>
      <tr>
        <th>View</th>
        <td>
          <v-btn @click="downloadScan()">
            <v-icon>picture_as_pdf</v-icon>View pdf
          </v-btn>
        </td>
      </tr>
      <tr>
        <th>Delete</th>
        <td>
          <v-btn @click="deleteScan()" color="error">
            <v-icon>delete</v-icon>Delete
          </v-btn>
        </td>
      </tr>
    </table>
    <p v-if="computedCount > 1">
      <a @click="rotate(-1)">rotate every page in document</a>
    </p>
    <v-btn @click="updateThumbnails" v-if="computedCount == 0">Generate thumbnails</v-btn>
    <p>
      <span>{{`${computedCount} pages`}}</span>
    </p>
    <div v-for="(scanPage, index) in scanPages" :key="index">
      <a @click="split(index)" v-if="index > 0">Split</a>
      <div class="scanImageSurround">
        <div>
          <span>{{index}}</span>
          <span>&#160;-&#160;</span>
          <span>{{scanPage.name}}</span>
          <span>&#160;-&#160;</span>
          <a @click="rotate(index)">Rotate</a>
        </div>
        <div>
          <img :src="`${computedThumbnailPrefix}?name=${scanPage.name}`" :alt="scanPage.name" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntityWorking from "@/components/link/EntityWorking";
import FileWorking from "@/components/link/FileWorking";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "Scan",
  metaInfo() {
    return {
      title: "Scan",
      titleTemplate: "%s - Document - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: { EntityWorking, FileWorking },
  computed: {
    computedScannerLink() {
      return {
        name: "scanner",
        params: { id: this.$route.params.id },
        query: {
          objectType: this.$route.query.objectType,
          objectId: this.$route.query.objectId
        }
      };
    },
    computedPrevious() {
      if (!this.navigation || !this.navigation.previous) {
        return undefined;
      }
      return {
        name: "scan",
        params: { id: this.printer.id },
        query: {
          name: this.navigation.previous.name,
          objectType: this.$route.query.objectType,
          objectId: this.$route.query.objectId
        }
      };
    },
    computedNext() {
      if (!this.navigation || !this.navigation.next) {
        return undefined;
      }
      return {
        name: "scan",
        params: { id: this.printer.id },
        query: {
          name: this.navigation.next.name,
          objectType: this.$route.query.objectType,
          objectId: this.$route.query.objectId
        }
      };
    },
    computedCount() {
      return this.scanPages ? this.scanPages.length : 0;
    },
    computedThumbnailPrefix() {
      return `${process.env.VUE_APP_CT_URL}/cors/admin/document/scanners/${this.$route.params.id}/thumbnails/chosen/download`;
    }
  },
  data() {
    return {
      entity: null,
      file: null,
      printer: null,
      scan: null,
      navigation: null,
      scanPages: null,
      descriptions: [],
      description: "",
      tracker: false,
      common: false,
      valid: false,
      descriptionRules: [v => !!v || "Description is required"]
    };
  },
  methods: {
    fetchEntity() {
      nbmlaw
        .get(`/cors/admin/directory/entities/${this.$route.query.objectId}`)
        .then(response => {
          // console.log(response.data);
          this.entity = response.data;
        });
    },
    fetchFile() {
      nbmlaw
        .get(`/cors/admin/file/files/${this.$route.query.objectId}`)
        .then(response => {
          // console.log(response.data);
          this.file = response.data;
        });
    },
    fetchPrinter() {
      nbmlaw
        .get(`/cors/admin/extras/printers/${this.$route.params.id}`)
        .then(response => {
          // console.log(response.data);
          this.printer = response.data;
        });
    },
    fetchScan() {
      nbmlaw
        .get(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen`,
          { params: { name: this.$route.query.name } }
        )
        .then(response => {
          // console.log(response.data);
          this.scan = response.data;
        });
    },
    fetchNavigation() {
      nbmlaw
        .get(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/navigation`,
          { params: { name: this.$route.query.name } }
        )
        .then(response => {
          // console.log(response.data);
          this.navigation = response.data;
        });
    },
    fetchScanPages() {
      nbmlaw
        .get(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/scanPages`,
          { params: { name: this.$route.query.name } }
        )
        .then(response => {
          // console.log(response.data);
          this.scanPages = response.data;
        });
    },
    completeDescription(value) {
      if (!value) {
        this.descriptions = [];
        return;
      }
      this.description = value;
      nbmlaw
        .get(`/cors/admin/document/descriptions/${value}`)
        .then(response => {
          // console.log(response.data)
          this.descriptions = response.data;
        });
    },
    deleteScan() {
      if (confirm("Are you sure?")) {
        nbmlaw
          .delete(
            `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen`,
            { params: { name: this.$route.query.name } }
          )
          .then(() => {
            // console.log(response.data);
            this.$router.push(this.computedScannerLink);
          });
      }
    },
    downloadScan() {
      window.open(
        `${process.env.VUE_APP_CT_URL}/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/download?name=${this.$route.query.name}`,
        "_blank"
      );
    },
    saveScan() {
      if (this.$refs.formUpload.validate()) {
        nbmlaw
          .post(
            `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen`,
            null,
            {
              params: {
                name: this.$route.query.name,
                objectType: this.$route.query.objectType,
                objectId: this.$route.query.objectId,
                description: this.description,
                tracker: this.tracker,
                common: this.common
              }
            }
          )
          .then(() => {
            // console.log(response.data);
            this.$router.push(this.computedScannerLink);
          });
      }
    },
    split(index) {
      nbmlaw
        .put(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/split`,
          null,
          {
            params: {
              name: this.$route.query.name,
              pageNo: index
            }
          }
        )
        .then(response => {
          // console.log(response.data);
          this.$router.push({
            name: "scan",
            params: { id: this.$route.params.id },
            query: {
              name: response.data,
              objectType: this.$route.query.objectType,
              objectId: this.$route.query.objectId
            }
          });
        });
    },
    rotate(index) {
      nbmlaw
        .put(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/rotate`,
          null,
          {
            params: {
              name: this.$route.query.name,
              pageNo: index
            }
          }
        )
        .then(() => {
          // console.log(response.data);
          this.update();
        });
    },
    updateThumbnails() {
      nbmlaw
        .put(
          `/cors/admin/document/scanners/${this.$route.params.id}/scans/chosen/thumbnails`,
          null,
          { params: { name: this.$route.query.name } }
        )
        .then(() => {
          // console.log(response.data);
          this.update();
        });
    },
    update() {
      this.fetchPrinter();
      this.fetchScan();
      this.fetchNavigation();
      this.fetchScanPages();
    }
  },
  watch: {
    "$route.query.name": {
      immediate: true,
      handler() {
        this.update();
      }
    },
    "$route.query.objectId": {
      immediate: true,
      handler() {
        this.entity = null;
        this.file = null;
        if (this.$route.query.objectId) {
          if (this.$route.query.objectType == "entity") {
            this.fetchEntity();
          } else if (this.$route.query.objectType == "file") {
            this.fetchFile();
          }
        }
      }
    }
  }
};
</script>
